import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PortfolioShowcase = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Fabrics', icon: 'Grid3X3' },
    { id: 'polyester', name: 'Polyester', icon: 'Sparkles' },
    { id: 'crepe', name: 'Natural Crepe', icon: 'Waves' },
    { id: 'velvet', name: 'Velvet Embroidery', icon: 'Award' },
    { id: 'special', name: 'Chinon & Shimmer', icon: 'Star' }
  ];

  const portfolioItems = [
    {
      id: 1,
      title: "Premium Polyester Fabric",
      category: "polyester",
      description: "High-quality polyester fabrics with excellent drape, durability, and vibrant color options for fashion and upholstery.",
      image: "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=400&h=300&fit=crop",
      technique: "Premium Finish",
      material: "100% Polyester",
      quantity: "MOQ: 100-150 meters"
    },
    {
      id: 2,
      title: "Natural Crepe Fabric",
      category: "crepe",
      description: "Elegant crepe fabric with distinctive crinkled texture, perfect for sarees, suits, and dresses.",
      image: "/assets/portfolio/natural-crape1.jpg",
      technique: "Textured Finish",
      material: "Natural Crepe",
      quantity: "MOQ: 100-150 meters"
    },
    {
      id: 3,
      title: "Velvet Embroidery Fabric",
      category: "velvet",
      description: "Luxurious velvet with exquisite embroidery work, ideal for bridal wear and premium ethnic garments.",
      image: "/assets/portfolio/velvet-1.png",
      technique: "Embroidery Work",
      material: "Premium Velvet",
      quantity: "MOQ: 50-100 meters"
    },
    {
      id: 4,
      title: "Chinon Chiffon Fabric",
      category: "special",
      description: "Lightweight, flowing chinon fabric with soft texture, perfect for dupattas, scarves, and ethnic wear.",
      image: "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=300&fit=crop",
      technique: "Soft Finish",
      material: "Chinon Chiffon",
      quantity: "MOQ: 50-100 meters"
    },
    {
      id: 5,
      title: "Shimmer Fabric Collection",
      category: "special",
      description: "Glamorous shimmer fabric with metallic sheen, perfect for party wear and festive occasions.",
      image: "https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=400&h=300&fit=crop",
      technique: "Metallic Finish",
      material: "Shimmer Georgette",
      quantity: "MOQ: 100-150 meters"
    }
  ];

  const filteredItems = activeCategory === 'all' 
    ? portfolioItems 
    : portfolioItems?.filter(item => item?.category === activeCategory);

  const handleViewPortfolio = () => {
    navigate('/portfolio');
  };

  const handleGetQuote = () => {
    navigate('/contact');
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-primary mb-4">
            Our Fabric Collection Showcase
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            Explore our diverse range of premium fabrics that demonstrate our quality, expertise, and commitment to excellence in manufacturing and wholesale supply.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                  activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground shadow-textile'
                    : 'bg-background text-text-secondary hover:bg-muted hover:text-primary'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span className="text-sm font-medium">{category?.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredItems?.map((item) => (
            <div
              key={item?.id}
              className="card-elevated group hover:shadow-heritage transition-all duration-300 animate-fade-in"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden rounded-t-lg h-64">
                <Image
                  src={item?.image}
                  alt={item?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-poppins font-semibold text-white mb-1">
                    {item?.title}
                  </h3>
                  <p className="text-sm text-white/80 line-clamp-2">
                    {item?.description}
                  </p>
                </div>
                <div className="absolute top-4 right-4 bg-golden text-golden-foreground px-2 py-1 rounded text-xs font-semibold">
                  {item?.technique}
                </div>
              </div>

              {/* Project Details */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-text-secondary mb-1">Material</div>
                    <div className="text-sm font-medium text-primary">{item?.material}</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-secondary mb-1">Quantity</div>
                    <div className="text-sm font-medium text-primary">{item?.quantity}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Eye" size={16} className="text-text-secondary" />
                    <span className="text-sm text-text-secondary">View Details</span>
                  </div>
                  <Icon 
                    name="ArrowUpRight" 
                    size={16} 
                    className="text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-textile rounded-2xl p-8 md:p-12">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-poppins font-bold text-white mb-4">
              Interested in Our Fabrics?
            </h3>
            <p className="text-lg text-white/80 mb-8">
              Discover our complete fabric collection and see how we can fulfill your wholesale or retail requirements with the same level of quality and competitive pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="btn-golden golden-accent"
                onClick={handleViewPortfolio}
                iconName="Package"
                iconPosition="left"
                iconSize={20}
              >
                View All Fabrics
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={handleGetQuote}
                iconName="MessageSquare"
                iconPosition="left"
                iconSize={20}
              >
                Get Wholesale Quote
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioShowcase;